import clamp from 'lodash/clamp';
import store from '@/store';

import { AnnotationFrame } from '@/types';
import { sortedTracks, removeTrack, imageData } from '../state';
import { setDrawingAnnotation, setSelectedAnnotation } from './annotation';
import { initializeTracksFromDataset } from '../utils';

export function changeFrame(frame: number) {
  const { dataset, currentFrameIndex } = store.state.dataset;
  const { currentAnnotation } = store.getters.dataset;
  if (!dataset || currentFrameIndex === null) { return; }

  // Dispatch frame change immediately, so following code is run on the correct frame
  store.dispatch.dataset.changeFrame(clamp(frame, 0, dataset.length - 1));

  /**
   * If there's an annotation selected, either make sure the same track is selected next frame,
   * or that no track is selected, if that track has no feature on the next frame.
   */
  if (currentAnnotation !== null) {
    const existingTrack = sortedTracks.value.find(
      (track) => track.getType()[0] === currentAnnotation.class && track.getFeature(frame)[0] !== null,
    );

    setSelectedAnnotation(existingTrack?.trackId ?? null, true);
    if (existingTrack === undefined) {
      setDrawingAnnotation(false);
    }
  }
}

export function deleteFrame(index: number) {
  const { dataset, currentFrameIndex } = store.state.dataset;
  if (dataset === null) {
    throw new Error('Cannot delete frame of null dataset!');
  }

  // If frame to delete is both the current frame and the last frame
  // in the dataset, set the new index to one less, as to not be out of bounds.
  if (index === currentFrameIndex && index === dataset.length - 1) {
    store.commit.dataset.setCurrentFrameIndex(index - 1);
  }

  // Remove frame
  store.commit.dataset.setDataset(
    dataset.slice(0, index).concat(dataset.slice(index + 1)),
  );

  // Reinitialize tracks
  initializeTracksFromDataset();
}

export function clearDatasetAnnotations() {
  const { dataset } = store.state.dataset;
  if (dataset === null) {
    throw new Error('Cannot clear annotations on null dataset!');
  }

  // Update just the dataset, and then update the tracks all at once,
  // instead of calling clearAnnotations for each frame individually,
  // as the latter has much more overhead and is unnecessary
  dataset.forEach(async (annotationFrame, index) => {
    const newAnnotationFrame: AnnotationFrame = {
      ...annotationFrame,
      annotations: {
        ...annotationFrame.annotations,
        regions: [],
      },
    };

    // Update dataset frame
    store.commit.dataset.updateFrame({ index, frame: newAnnotationFrame });
  });

  // Update tracks in one shot
  initializeTracksFromDataset();
}

export async function fetchDataset(datasetId: string, user: boolean) {
  const { client } = store.state;
  const { applicationData } = store.state.application;
  if (client === null || applicationData === null) { return; }

  store.commit.dataset.setLoading(true);
  try {
    const fullDataset = await client.getRemoteDataset(datasetId, user);
    store.commit.dataset.setMetadata(fullDataset[0].reference);
    store.commit.dataset.setUserDataset(user);
    store.commit.dataset.setDataset(fullDataset);
    store.commit.dataset.setDatasetId(datasetId);
    store.commit.dataset.setValidationOnly(fullDataset[0].validation_only);
    store.commit.dataset.setCurrentFrameIndex(0);
    store.commit.dataset.setStudyType(fullDataset[0].study_type);

    // Initialize tracks
    initializeTracksFromDataset();
  } finally {
    store.commit.dataset.setLoading(false);
  }
}

export function clearDataset() {
  // Store
  store.dispatch.dataset.clearDataset();

  // Local
  imageData.value = null;
  sortedTracks.value.forEach((t) => removeTrack(t.trackId));
}
